import { signInWithCustomToken, User } from 'firebase/auth';
import { auth } from './firebase';

// Legacy searchtundra.com hostnames and the enviro.ai origin each should land on.
// searchtundra.com is being retired; every visitor is migrated to enviro.ai.
// The test.searchtundra.com pair lives on the separate test Firebase project,
// so its custom-token hand-off stays within that project's auth pool.
const HOST_REDIRECTS: Record<string, string> = {
  'searchtundra.com': 'https://app.enviro.ai',
  'www.searchtundra.com': 'https://app.enviro.ai',
  'test.searchtundra.com': 'https://test.enviro.ai',
  'www.test.searchtundra.com': 'https://test.enviro.ai',
};

// Fragment key used to carry the one-time sign-in token across domains. A URL
// fragment is never sent to the server (so it stays out of access logs), and we
// wipe it from history the instant we arrive.
const SSO_TOKEN_KEY = 'sso_token';

const endpoint = (path: string): string => `${window.location.origin}${path}`;

// Resolves once Firebase has restored (or confirmed the absence of) a persisted
// session from IndexedDB.
const waitForAuthInit = (): Promise<User | null> =>
  new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });

// On a legacy domain: hand the current session off to the enviro.ai origin (so
// the user is not asked to log in again) and redirect. Returns true to signal
// the caller to halt app rendering because we are navigating away.
const redirectFromLegacyDomain = async (target: string): Promise<boolean> => {
  const path = window.location.pathname + window.location.search;

  const user = await waitForAuthInit();
  if (user) {
    try {
      const idToken = await user.getIdToken();
      const res = await fetch(endpoint('/exchange-token'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      });
      if (res.ok) {
        const { customToken } = await res.json();
        window.location.replace(
          `${target}${path}#${SSO_TOKEN_KEY}=${encodeURIComponent(customToken)}`
        );
        return true;
      }
    } catch (_) {
      // Fall through to a plain redirect; worst case the user logs in once.
    }
  }

  window.location.replace(`${target}${path}`);
  return true;
};

// On the destination domain: if we arrived carrying a hand-off token, sign in
// silently before the app renders so there is no login screen flash.
const consumeHandoffToken = async (): Promise<void> => {
  const match = window.location.hash.match(
    new RegExp(`${SSO_TOKEN_KEY}=([^&]+)`)
  );
  if (!match) return;

  const customToken = decodeURIComponent(match[1]);
  // Strip the token from the URL immediately so it never lingers in history.
  window.history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search
  );

  try {
    const credential = await signInWithCustomToken(auth, customToken);
    const idToken = await credential.user.getIdToken();
    // Establish the .enviro.ai session cookie (also shared with *.enviro.ai
    // subdomains, e.g. the aermod3d iframe).
    await fetch(endpoint('/set-session'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });
  } catch (_) {
    // Expired/invalid token: fall through to the normal login flow.
  }
};

/**
 * Runs the searchtundra.com -> enviro.ai migration before the app renders.
 * Returns true when the page is redirecting away (caller should skip rendering).
 */
export const runDomainMigration = async (): Promise<boolean> => {
  const target = HOST_REDIRECTS[window.location.hostname];
  if (target) {
    return redirectFromLegacyDomain(target);
  }
  await consumeHandoffToken();
  return false;
};
